import React, { useState } from 'react';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import toast from 'react-hot-toast';
import { useRecoilState, useRecoilValue } from 'recoil';

import routes from '@/services/routes';
import { handleFetchErrors } from '@/services/apollo';
import Loader from '@/components/commons/loader';
import { onboardingAtom, userAtom } from '@/components/with_auth/State';
import OnboardingTooltip from '@/components/onboarding/OnboardingTooltip';
import Pagination from '@/components/commons/pagination';
import WarningCard from '@/components/commons/warning_card';

import { UPDATE_USER } from '@/components/onboarding/Actions';
import AgendasCard from '@/components/calendar/agendas/AgendasCard';
import {
    UPDATE_AGENDA, SEND_EXISTING_AGENDA_MAIL,
} from './Actions';

interface Props {
    readonly vacationMode: boolean;
    readonly loading: boolean;
    readonly isError: boolean;
    readonly refetch: () => void;
    readonly agendas: Array<any>;
}

const allowPageSizeOptions = false;
const paginationState = null;
const setParentPage = () => { };
const selectedSize = 25;
const setSelectedSize = () => { };
const pageSizeOptions = [];

function AgendasGrid({
    vacationMode, refetch, loading, agendas, isError,
}: Props) {
    const router = useRouter();

    const [page, setPage] = useState(0);
    const [sendExistingAgendaMail] = useMutation(SEND_EXISTING_AGENDA_MAIL);
    const [updateUser] = useMutation(UPDATE_USER);
    const [updateAgenda] = useMutation(UPDATE_AGENDA);
    const [formError, setFormError] = useState('');

    const [user, setUserRecoil] = useRecoilState(userAtom);
    const onboardingData = useRecoilValue(onboardingAtom);

    /*   This function should return somewhere, but for now it is out of the figma so it goes.
    const handleDeleteAgenda = async (id) => {
        try {
            const response = await deleteAgenda({
                variables: { deleteAgendaMailInput: { id } },
            });
            refetch();
            if (response.data.deleteAgendaMail.agendaMail) {
                toast.success('Agenda removed successfully.');
            } else {
                toast.error('Something went wrong.  Please try again.');
            }
        } catch (err) {
            handleFetchErrors(err);
        }
    }; */

    const handleUpdateAgenda = async (_id, enabled, record) => {
        try {
            const response = await updateAgenda({
                variables: {
                    updateAgendaMailInput: {
                        id: record.id,
                        attributes: {
                            enabled,
                        },
                    },
                },
            });
            refetch();

            if (response.data.updateAgendaMail.agendaMail) {
                toast.success('Agenda updated successfully.');
                if (formError !== '') setFormError('');
            } else {
                setFormError('Something went wrong.  Please try again.');
            }
        } catch (err) {
            handleFetchErrors(err);
            setFormError('An unexpected error occurred.  Please try again later.');
        }
    };

    const handleSendExistingAgendaMail = async (id) => {
        try {
            const response = await sendExistingAgendaMail({
                variables: {
                    sendExistingAgendaMailInput: {
                        id,
                    },
                },
            });
            if (response.data.sendExistingAgendaMail.sentSuccessfully) {
                toast.success('Email sent successfully.');
                if (formError !== '') setFormError('');
                // if we're on the last step on onboarding and the user
                // pushes the send now, then complete the onboarding
                // Only do this if has created agendas is false!!
                if (onboardingData.agenda === 4 && !user?.hasCreatedAgendas) {
                    // Do we want to tell the user if the onboarding check failed
                    // on graphqls side?
                    const userResponse = await updateUser({
                        variables: {
                            updateUserInput: {
                                attributes: {
                                    hasCreatedAgendas: true,
                                },
                            },
                        },
                    });
                    if (userResponse.data.updateUser.user) {
                        const { user: newUser } = userResponse.data.updateUser;
                        setUserRecoil(newUser);
                        router.push(routes.MY_SIXTY);
                    } else if (userResponse.data.updateUser.errors) {
                        const errors = userResponse.data.updateUser.errors.map((error) => error.message).join(', ');
                        setFormError(errors);
                    } else {
                        setFormError('An unknown error occurred.  Please try again later.');
                    }
                }
            } else {
                setFormError('Failed to send email successfully');
                throw new Error('Failed to send email successfully');
            }
        } catch (err) {
            handleFetchErrors(err);
            setFormError('An unexpected error occurred. Please try again later.');
        }
    };

    const pageSize = selectedSize;

    const totalPages = agendas && agendas.length ? Math.ceil(agendas.length / pageSize) : 0;

    const getDisplayedData = (idx, rows) => {
        if (!rows) {
            return [];
        }
        const start = idx * pageSize;
        return rows.slice(start, start + pageSize);
    };

    const renderPagination = () => {
        if (allowPageSizeOptions && paginationState) {
            return (
                <div className="flex items-center justify-center py-6">
                    <Pagination
                        initialPage={page}
                        totalPages={totalPages}
                        // windowSize={paginationState.windowSize}
                        onPageChange={(selectedPage) => {
                            setParentPage();
                            setPage(selectedPage);
                        }}
                        allowPageSizeOptions={allowPageSizeOptions}
                        selectedSize={selectedSize}
                        pageSizeOptions={pageSizeOptions}
                        setSelectedSize={setSelectedSize}
                    />
                </div>
            );
        }

        if (paginationState && totalPages > 1) {
            return (
                <div className="flex justify-center py-6">
                    <Pagination
                        initialPage={page}
                        totalPages={totalPages}
                        // windowSize={paginationState.windowSize}
                        onPageChange={setPage}
                    />
                </div>
            );
        }

        return null;
    };

    return (
        <Loader isLoading={loading && !agendas} isError={isError}>
            {
                formError !== '' && (
                    <div className="w-full my-[20px]">
                        <WarningCard message={formError} />
                    </div>
                )
            }

            <div
                className="flex flex-col flex-wrap justify-between gap-4 md:grid md:grid-cols-2 xl:grid-cols-3"
                data-onboarding="agenda-step-2"
            >
                {
                    agendas?.length > 0
                    && getDisplayedData(page, agendas).map((agenda) => (
                        <AgendasCard
                            key={agenda.id}
                            record={agenda}
                            vacationMode={vacationMode}
                            handleUpdateAgenda={handleUpdateAgenda}
                            handleSendExistingAgendaMail={handleSendExistingAgendaMail}
                            user={user}
                            onboardingData={onboardingData}
                            externalStyles="flex flex-col h-full w-full justify-center items-center text-cleanSlate p-5 gap-1"
                        />
                    ))
                }
            </div>
            {renderPagination()}
            <Tooltip id="tip" />
            <OnboardingTooltip step={2} />
            <OnboardingTooltip step={4} />
        </Loader>
    );
}

export default AgendasGrid;
